// AdminPanel/DltFinder — the Search tab's "DLT ID" lookup: given a principal
// entity id off a compliance query, find the customers registered under it.
//
// Backed by ListOrganizationsV3 (GET /api/v3/organizations?search=), matching
// `t.config->>'dlt_principal_entity_id'` EXACTLY. There is no column for it —
// UpdateTenantDltId writes it into the tenants.config JSONB — so this is a key
// lookup, not an indexed one.
//
// Exact, not partial: 247 tenants share the placeholder 1234567890123456789,
// and a fragment would drag all of them back. Many-to-one is still normal
// though — a reseller registers one DLT id and onboards several customers
// under it.
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Search} from '@mui/icons-material';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography
} from '@mui/material';

import {adminLookupOrgs} from '../../actions/adminPanel';
import {validateDltId} from './orgSearch';
import OrgResultCard from './OrgResultCard';

class DltFinder extends Component {
  state = {query: '', searching: false, results: null, error: null};

  // Guards against a stale response landing after a newer search (or
  // unmount) — only the latest request may write state.
  searchSeq = 0;

  componentWillUnmount() {
    this.searchSeq++;
  }

  search = async () => {
    const check = validateDltId(this.state.query);
    if (!check.ok) {
      this.setState({error: check.error, results: null});
      return;
    }

    const seq = ++this.searchSeq;
    this.setState({searching: true, results: null, error: null});
    try {
      const orgs = await adminLookupOrgs(check.value);
      if (this.searchSeq !== seq) return;
      this.setState({searching: false, results: orgs});
    } catch (err) {
      if (this.searchSeq !== seq) return;
      const status = err?.response?.status;
      const msg = status === 403
        ? 'You don\'t have permission to use admin search.'
        : err?.response?.data?.message || err?.message || 'Search failed — please try again.';
      this.setState({searching: false, error: msg});
    }
  };

  render() {
    const {query, searching, error, results} = this.state;
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          Find the customers registered under a DLT principal entity ID. The id
          is matched exactly — a partial will not match. One DLT id can cover
          several tenants, so expect a list.
        </Typography>
        <Box sx={{display: 'flex', gap: 1, mb: 2}}>
          <TextField
            fullWidth
            size="small"
            label="DLT principal entity ID"
            placeholder="1701174731403059285"
            value={query}
            onChange={(e) => this.setState({query: e.target.value, error: null})}
            onKeyDown={(e) => { if (e.key === 'Enter') this.search(); }}
            disabled={searching}
          />
          <Button
            variant="contained"
            onClick={this.search}
            disabled={searching}
            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <Search />}
            sx={{flexShrink: 0}}
          >
            Search
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
        {results && results.length === 0 && (
          <Alert severity="info">
            No customer is registered under that DLT ID. It is matched exactly — check the complete value was pasted.
          </Alert>
        )}
        {results && results.map((o) => (
          <OrgResultCard key={o.id} org={o} orgs={this.props.orgs} onOpenOrg={this.props.onOpenOrg} />
        ))}
      </Box>
    );
  }
}

DltFinder.propTypes = {
  orgs: PropTypes.array,
  onOpenOrg: PropTypes.func.isRequired
};

export default DltFinder;
